import {
  Navigate,
  NavLink,
  Outlet,
  useNavigate,
} from "react-router-dom";
import { useState } from "react";

import { useAuth } from "@/lib/auth";

import {
  LayoutDashboard,
  CalendarClock,
  CalendarDays,
  ScanFace,
  Users,
  Package,
  Image as ImgIcon,
  LogOut,
  Menu,
  X,
  Database,
  MessageSquareText,
} from "lucide-react";


/* =========================================================
   SIDEBAR NAVIGATION
   adminOnly: true = visible only to admin
========================================================= */

const NAV = [
  {
    to: "/admin",
    label: "Dashboard",
    icon: LayoutDashboard,
    end: true,
  },

  {
    to: "/admin/bookings",
    label: "Bookings",
    icon: CalendarClock,
    adminOnly: true,
  },

  {
    to: "/admin/calendar",
    label: "Calendar",
    icon: CalendarDays,
  },

  {
    to: "/admin/events",
    label: "Events (AI)",
    icon: ScanFace,
  },

  {
    to: "/admin/team",
    label: "Team",
    icon: Users,
    adminOnly: true,
  },

  {
    to: "/admin/packages",
    label: "Packages",
    icon: Package,
  },

  {
    to: "/admin/gallery",
    label: "Portfolio",
    icon: ImgIcon,
  },
  { to: "/admin/reviews", label: "Review Management", icon: MessageSquareText, adminOnly: true },
  {
    to: "/admin/master-data",
    label: "Master Data",
    icon: Database,
    superAdminOnly: true,
  },
];


/* =========================================================
   ADMIN LAYOUT
========================================================= */

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Debug
  console.log("AdminLayout user:", user);
  console.log("AdminLayout role:", user?.role);

  /* -------------------------------------------------------
     Wait until authentication has finished loading
  ------------------------------------------------------- */

  if (!ready) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-neutral-500">
        Loading…
      </div>
    );
  }

  /* -------------------------------------------------------
     Not logged in
  ------------------------------------------------------- */

  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  /* -------------------------------------------------------
     Check role
  ------------------------------------------------------- */

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = user?.role === "super_admin";

  console.log("Is Admin:", isAdmin);


  /* -------------------------------------------------------
     Filter navigation based on role
  ------------------------------------------------------- */

  const visibleNav = NAV.filter((item) => {
    if (item.superAdminOnly) return isSuperAdmin;
    // Normal menu item
    if (!item.adminOnly) {
      return true;
    }

    // Admin-only item
    return isAdmin;
  });


  /* =======================================================
     UI
  ======================================================= */

  return (
    <div className="min-h-screen bg-[#050505] text-white flex admin-shell">
      <div className="admin-mobile-topbar">
        <div className="font-serif-display text-lg">Wedding<span className="text-[#E11D2A]">·</span>Touch</div>
        <button onClick={() => setMobileOpen((v) => !v)} aria-label="Toggle admin menu">{mobileOpen ? <X size={22}/> : <Menu size={22}/>}</button>
      </div>

      {/* ===================================================
          SIDEBAR
      =================================================== */}

      <aside
        data-testid="admin-sidebar"
        className={`w-64 shrink-0 bg-[#08080a] border-r border-white/5 min-h-screen flex flex-col admin-sidebar ${mobileOpen ? "admin-sidebar-open" : ""}`}
      >

        {/* Logo */}

        <div className="px-6 py-8">
          <div className="font-serif-display text-xl">
            Wedding
            <span className="text-[#E11D2A]">·</span>
            Touch
          </div>

          <div className="overline mt-2 text-neutral-500">
            Studio Console
          </div>
        </div>


        {/* =================================================
            NAVIGATION
        ================================================= */}

        <nav className="px-3 flex-1 space-y-1">

          {visibleNav.map((item) => {
            const Icon = item.icon;

            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`sidebar-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm transition-colors rounded-sm ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-neutral-400 hover:bg-white/5 hover:text-white"
                  }`
                }
                onClick={() => setMobileOpen(false)}
              >

                <Icon
                  size={16}
                  strokeWidth={1.5}
                />

                <span>{item.label}</span>

              </NavLink>
            );
          })}

        </nav>


        {/* =================================================
            USER DETAILS / LOGOUT
        ================================================= */}

        <div className="p-4 border-t border-white/5">

          <div className="px-2 pb-3">

            <div className="text-sm text-white">
              {user?.name}
            </div>

            <div className="text-xs text-neutral-500 truncate">
              {user?.email}
            </div>

            <div className="overline mt-1 text-[#E11D2A]">
              {user?.role}
            </div>

          </div>


          {/* Logout */}

          <button
            data-testid="admin-logout"
            onClick={async () => {
              await logout();
              nav("/admin/login");
            }}
            className="w-full flex items-center gap-2 px-4 py-2 text-sm text-neutral-400 hover:bg-white/5 hover:text-white transition-colors rounded-sm"
          >
            <LogOut size={14} />
            Sign out
          </button>

        </div>

      </aside>


      {/* ===================================================
          PAGE CONTENT
      =================================================== */}

      <main className="flex-1 min-w-0 admin-main">
        <Outlet />
      </main>

    </div>
  );
}